import { Paging } from '@Core';
import {
    ICancelGroupPcnForChangePlate,
    ICancelGroupPotentialContraventionDetail,
    IGetGroupPotentialContraventionIds,
    VRNDetail,
} from '@EcamModel/controllers/IGroupPotentialContraventionHttpController';
import {
    Camera,
    ContraventionStatus,
    OutCome,
    GroupPotentialContraventions,
    PcnDuplicateWithChangePlate,
    PermitData,
    PotentialContraventionLookUp,
    PotentialContraventionWithInfo,
} from '@EcamModel/model';
import { CameraNotes, CameraNoteType } from '@EcamModel/model/CameraNotes';
import { AiConfidenceLevel } from '@EcamModel/model/GroupPotentialContraventions';
import { Zones } from '@EcamModel/model/Zones';
import { pushError, pushSuccess, pushWarning } from '@components/toast';
import {
    cameraNoteHttpController,
    cancelationReasonController,
    pcController,
    potentialGroupContraventionController,
} from '@controllers/index';
import { Filter } from '@pages/cameras/list';
import { useVerificationQueueMode, VerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useThrottle } from 'rooks';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { LocationState, PCGroup, PcGroupStateItem, PcGroupStatus, PCInfo } from '..';
import useModal from './useModal';
import { appConfig } from '@configs/index';

type Props = {
    readonly?: boolean;
};

export const cancellationReasonMO = appConfig.contraventionReasonId.cancellationReasonMultipleOffender;
export const notesMO = 'The Multiple Offenders limit has been reached for this VRN';

export const groupPcQueryKey = (id: number) => ['groupPotentialContravention', id];
const PREFETCH_STALE_TIME = 2 * 60 * 1000;

export default function usePotentialContraventionContext(props: Props = {}) {
    const { readonly } = props;
    const params = useParams<{ contraventionId: string; groupPcId: string }>();
    const location = useLocation();
    const state: LocationState | null = location.state;
    const backdrop = useBackdrop();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { verificationQueueMode } = useVerificationQueueMode();

    const [searchParams] = useSearchParams();
    const contraventionReason = searchParams.get('contraventionReason');
    const fromDate = searchParams.get('fromDate')
        ? moment(searchParams.get('fromDate'), 'DD-MM-YYYY HH:mm:ss').toDate()
        : null;
    const toDate = searchParams.get('toDate')
        ? moment(searchParams.get('toDate'), 'DD-MM-YYYY HH:mm:ss').toDate()
        : null;
    const zonesIds = searchParams.get('zones') ? searchParams.get('zones')!.split(',').map(Number) : [];
    const emailVerifier = searchParams.get('emailVerifier');
    const contraventionId = parseInt(params.contraventionId || params.groupPcId || '-1');
    const contraventionIdRef = useRef(contraventionId);
    useEffect(() => {
        contraventionIdRef.current = contraventionId;
    }, [contraventionId]);
    const contraventionDateFrom = searchParams.get('contraventionDateFrom') ? moment(searchParams.get('contraventionDateFrom')).toDate() : null;
    const contraventionDateTo = searchParams.get('contraventionDateTo') ? moment(searchParams.get('contraventionDateTo')).toDate() : null;
    const isFilterPcLackVrn = searchParams.get('isFilterPcLackVrn') ? Boolean(searchParams.get('isFilterPcLackVrn')) : null;
    
    const isFilterPcGroupMatched = searchParams.get('isFilterPcGroupMatched')
        ? Boolean(searchParams.get('isFilterPcGroupMatched'))
        : null;

    const hasPlate = searchParams.get('hasPlate');
    const confidence = searchParams.get('confidence') ? searchParams.get('confidence')!.split(',').map((v) => v as AiConfidenceLevel) : null;

    const [isLoadingLookUp, setIsLoadingLookUp] = useState(false);
    const [isShowLookup, setIsShowLookup] = useState(false);
    const [isShowForeignPlate, setIsShowForeignPlate] = useState(false);
    const [duplicate, setDuplicate] = useState<PcnDuplicateWithChangePlate>({
        pcnDuplicate: [],
        isChangePlate: false,
    } as PcnDuplicateWithChangePlate);
    const popupChangePlace = useModal();

    const [groupPcIds, setGroupPcIds] = useState<PcGroupStateItem[]>(state?.pcIds?.map((id) => ({ Id: id })) || []);
    const [groupPc, setGroupPc] = useState<PCGroup>();
    const [selectedPc, setSelectedPc] = useState<PCInfo>();
    const [isLatestItem, setIsLatestItem] = useState(false);
    const [updatingPlate, setUpdatingPlate] = useState(false);

    const [vehicleLookup, setVehicleLookup] = useState<PotentialContraventionLookUp>(
        {} as PotentialContraventionLookUp
    );

    const [vrnPlate, setVrnPlate] = useState(selectedPc?.Plate);

    const [isForeignPlate, setIsForeignPlate] = useState(false);
    const [updateChange, setUpdateChange] = useState(false);

    const step = groupPc?.PotentialContraventions?.[0].Step;
    const zoneId = groupPc?.PotentialContraventions?.[0].ZoneId;

    const [conditionExemptionsEnabled, setConditionExemptionsEnabled] = useState<boolean>(false);

    const [filter, setFilter] = useState<Partial<Filter>>({
        page: 1,
        from: undefined,
        to: undefined,
        search: '',
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [listNotes, setListNotes] = useState<Paging<CameraNotes>>({} as Paging<CameraNotes>);
    const [selectedCamera, setSelectedCamera] = useState<Camera>({} as Camera);

    const prefetchNextGroupPc = useCallback(
        async (currentId: number, ids: PcGroupStateItem[]) => {
            const currentIdx = ids.findIndex((gr) => gr.Id === currentId);
            let foundId: number | undefined;
            for (let i = currentIdx + 1; i < ids.length; i++) {
                if (!ids[i].Status) {
                    foundId = ids[i].Id;
                    break;
                }
            }
            if (!foundId) return;
            const nextId: number = foundId;

            const nextGroupPc = await queryClient.fetchQuery(
                groupPcQueryKey(nextId),
                () => potentialGroupContraventionController.getGroupPotentialContravention(nextId),
                { staleTime: PREFETCH_STALE_TIME }
            );

            if (!nextGroupPc) return;

            if (nextGroupPc.NoPCRestriction?.isNoPcRestriction && nextGroupPc.OutCome === OutCome.InProgress) {
                return;
            }

            const nextPc = nextGroupPc.PotentialContraventions?.[0];
            if (!nextPc?.Plate) return;

            const hasVehicleInfo = nextPc.VehicleColor || nextPc.VehicleFuelType || nextPc.VehicleMake || nextPc.VehicleModel;
            if (hasVehicleInfo) return;

            try {
                const lookupRes = await pcController.lookUp(nextPc.Plate, { GroupPotentialContraventionId: nextId });
                const hasData = lookupRes.Make || lookupRes.Model || lookupRes.Color || lookupRes.FuelType;
                if (!hasData) return;

                const isChanged =
                    nextPc.VehicleColor !== lookupRes.Color ||
                    nextPc.VehicleMake !== lookupRes.Make ||
                    nextPc.VehicleModel !== lookupRes.Model ||
                    nextPc.VehicleFuelType !== lookupRes.FuelType;

                if (isChanged) {
                    const isAudit = location.pathname.includes('audit');
                    await potentialGroupContraventionController.doChangePlateGroupPotentialContravention({
                        Id: nextId,
                        Detail: {
                            Vrn: nextPc.Plate,
                            Make: lookupRes.Make,
                            Model: lookupRes.Model,
                            Color: lookupRes.Color,
                            FuelType: lookupRes.FuelType,
                        },
                        IsVerificationAudit: isAudit,
                    });
                    queryClient.removeQueries(groupPcQueryKey(nextId));
                    queryClient.prefetchQuery(
                        groupPcQueryKey(nextId),
                        () => potentialGroupContraventionController.getGroupPotentialContravention(nextId),
                        { staleTime: PREFETCH_STALE_TIME }
                    );
                }
            } catch {
            }
        },
        [queryClient]
    );

    const prefetchPrevGroupPc = useCallback(
        async (currentId: number, ids: PcGroupStateItem[]) => {
            const currentIdx = ids.findIndex((gr) => gr.Id === currentId);
            let foundId: number | undefined;
            for (let i = currentIdx - 1; i >= 0; i--) {
                if (!ids[i].Status) {
                    foundId = ids[i].Id;
                    break;
                }
            }
            if (!foundId) return;
            const prevId: number = foundId;

            await queryClient.fetchQuery(
                groupPcQueryKey(prevId),
                () => potentialGroupContraventionController.getGroupPotentialContravention(prevId),
                { staleTime: PREFETCH_STALE_TIME }
            );
        },
        [queryClient]
    );

    const handleGetDetailGroupPotentialContravention = async (permitDataOverride?: PermitData) => {
        const cached = queryClient.getQueryData<GroupPotentialContraventions>(groupPcQueryKey(contraventionId));
        if (!cached) {
            backdrop.setTrue();
        }

        const fetchPromise = cached
            ? Promise.resolve(cached)
            : potentialGroupContraventionController.getGroupPotentialContravention(contraventionId);

        await fetchPromise
            .then((res) => {
                if (!res) {
                    navigate(`/${verificationQueueMode}`);
                    return;
                }

                if (!cached) {
                    queryClient.setQueryData(groupPcQueryKey(contraventionId), res);
                }

                const orderedPcs = orderBy(res.PotentialContraventions, (pc) => -pc.Step);
                const _res: GroupPotentialContraventions = {
                    ...res,
                    PotentialContraventions: permitDataOverride != null
                        ? orderedPcs?.map((pc, idx) => idx === 0 ? { ...pc, PermitData: permitDataOverride } : pc)
                        : orderedPcs,
                };
                console.log(`_res:`, _res);
                setGroupPc(_res);
                setSelectedPc(_res.PotentialContraventions![0] as PCInfo);
                setSelectedCamera(_res?.Zone?.Cameras?.[0]!);
                const permitExemptionsEnabled = checkPermitExemptionsEnabled(_res.Zone!);
                setConditionExemptionsEnabled(permitExemptionsEnabled);
            })
            .catch((err) => navigate(`/${verificationQueueMode}`))
            .finally(() => backdrop.setFalse());
    };

    const checkPermitExemptionsEnabled = (zone: Zones) => {
        if (!zone || !zone.Cameras) {
            return false;
        }
        for (const camera of zone.Cameras) {
            if (camera.GeoZones) {
                for (const geoZone of camera.GeoZones) {
                    if (geoZone.PermitExemptionsEnabled) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    const fetchCancellationReasons = async () => {
        const response = await cancelationReasonController.list({ pageSize: 10000 });
        return response.rows;
    };

    const { data: cancellationReasons, isLoading: isLoadingCancellationReasons } = useQuery(
        'cancellationReasons',
        fetchCancellationReasons,
        {
            // initialData: [], // Dữ liệu mặc định khi đang loading
            // staleTime: Infinity,
            // enabled: !readonly,
            staleTime: 60 * 60 * 1000,
        }
    );
    console.log(`cancellationReasons:`, readonly);

    const updateGrPcIds = (id: number, status: PcGroupStatus) => {
        setGroupPcIds((prev) => {
            return prev.map((p) => (p.Id === id ? { ...p, Status: status } : p));
        });
    };

    const handleNext = (currentId?: number) => {
        const currentPcId = currentId ?? groupPc?.Id;
        const currentIdx = groupPcIds.findIndex((gr) => gr.Id === currentPcId);

        let nextId: number | undefined;
        for (let i = currentIdx + 1; i < groupPcIds.length; i++) {
            if (!groupPcIds[i].Status) {
                nextId = groupPcIds[i].Id;
                break;
            }
        }

        if (currentPcId) {
            queryClient.removeQueries(groupPcQueryKey(currentPcId));
        }

        if (!nextId) {
            setIsLatestItem(true);
            pushWarning('This item is the last item in the list');
            backdrop.setTrue();
            setTimeout(() => {
                backdrop.setFalse();
                navigate(`/${verificationQueueMode}${location.search}`, { replace: true });
            }, 2000);
            return;
        } else {
            navigate(`/${verificationQueueMode}/contravention/${nextId}${location.search}`, { replace: true });
        }
    };

    const handlePrev = () => {
        const currentPcId = groupPc?.Id;
        const currentIndex = groupPcIds.findIndex((gr) => gr.Id === currentPcId);

        let prevId: number | undefined;
        for (let i = currentIndex - 1; i >= 0; i--) {
            if (!groupPcIds[i].Status) {
                prevId = groupPcIds[i].Id;
                break;
            }
        }

        if (!prevId) {
            pushWarning('This is the first item in the list');
            return;
        }

        navigate(`/${verificationQueueMode}/contravention/${prevId}${location.search}`, { replace: true });
    };

    const handleApprove = (pc: PCInfo) => {
        backdrop.setTrue();
        potentialGroupContraventionController
            .doApproveGroupPotentialContravention({ PotentialContraventionId: pc.Id!, IsForeignPlate: pc.IsForeign })
            .then((res: boolean | PermitData) => {
                if ((res as PermitData).hasPermit) {
                    pushWarning('Has permit');
                    setSelectedPc((prev) => ({
                        ...prev!,
                        PermitData: res as PermitData,
                    }));
                    backdrop.setFalse();
                    return;
                }
                queryClient.invalidateQueries(['trailAuditEventLogPCN']);
                updateGrPcIds(groupPc!.Id!, 'Approve');
                pushSuccess('Approve PCN successfully');
                handleNext();
            })
            .catch((error) => {
                pushError(error?.response?.data?.message || 'Approve PCN failed');
                queryClient.removeQueries(
                    groupPcQueryKey(pc.GroupPotentialContraventionId ?? groupPc?.Id ?? contraventionId)
                );
                handleGetDetailGroupPotentialContravention();
                backdrop.setFalse();
            });
    };
    const [throttleApprove] = useThrottle(handleApprove, 2000);

    const handleCancel = (props: ICancelGroupPotentialContraventionDetail) => {
        backdrop.setTrue();
        potentialGroupContraventionController
            .doCancelGroupPotentialContravention(props)
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEventLogPCN']);
                updateGrPcIds(props.Id, 'Cancel');
                pushSuccess('Cancel PCN successfully ');
                handleNext();
            })
            .catch(() => {
                backdrop.setFalse();
            });
    };
    const [throttleCancel] = useThrottle(handleCancel, 2000);

    const handleChangePc = (pc: Partial<PCInfo>) => {
        setGroupPc((prev) => ({
            ...prev!,
            PotentialContraventions: prev?.PotentialContraventions?.map((_) => ({ ..._, ...pc })),
        }));
        setSelectedPc((prev) => ({ ...prev!, ...pc }));
    };

    const { pathname } = location;
    const conditionAudit = pathname.includes('audit');

    const handleChangePlate = async (vrn: VRNDetail, isForeign?: boolean) => {
        const calledForId = groupPc!.Id!;
        setUpdatingPlate(true);
        await potentialGroupContraventionController
            .doChangePlateGroupPotentialContravention({
                Id: calledForId,
                Detail: vrn,
                IsForeignPlate: isForeign,
                IsVerificationAudit: conditionAudit,
            })
            .then(async (res) => {
                if (contraventionIdRef.current !== calledForId) return;

                queryClient.invalidateQueries(['trailAuditEventLogPCN']);
                setGroupPc((prev) => ({
                    ...prev!,
                    PotentialContraventions: (prev?.PotentialContraventions as PotentialContraventionWithInfo[]).map(
                        (_) => ({ ..._, PCNDuplicate: res.pcnDuplicate })
                    ),
                }));

                handleChangePc({
                    Plate: vrn.Vrn,
                    VehicleMake: vrn.Make,
                    VehicleColor: vrn.Color,
                    VehicleModel: vrn.Model,
                    IsForeign: isForeign,
                    PermitData: res.PermitData,
                    PCNDuplicate: res.pcnDuplicate
                });
                popupChangePlace.handleClose();

                if (!res.pcnDuplicate.length && !res.PermitData?.hasPermit) {
                    pushSuccess('Filled successfully!');
                }
                if (res.pcnDuplicate.length) {
                    pushWarning('Has duplicate');
                }
                if (res.PermitData?.hasPermit) {
                    pushWarning('Has permit');
                }
                if (res.PermitData?.permitInfo?.isGlobalPermit) {
                    pushWarning('Has global permit');
                }

                queryClient.removeQueries(groupPcQueryKey(calledForId));
                await handleGetDetailGroupPotentialContravention(res.PermitData);
            })
            .catch(() => {
                backdrop.setFalse();
            })
            .finally(() => {
                setUpdatingPlate(false);
            });
    };

    const handleChangeForeign = (isForeign: boolean) => {
        handleChangePc({ IsForeign: isForeign });
    };

    const skippedList = groupPcIds.filter((gr) => !gr.Status);

    const currentIndex = groupPcIds.findIndex((gr) => gr.Id === groupPc?.Id);
    const canGoPrev = currentIndex > 0 && groupPcIds.slice(0, currentIndex).some((gr) => !gr.Status);

    const handleContinue = () => {
        setIsLatestItem(false);
        setGroupPcIds(skippedList);
        navigate(`/verification/contravention/${skippedList[0].Id}`, { replace: true });
    };

    const handleCancelDuplicate = async (props: ICancelGroupPcnForChangePlate) => {
        backdrop.setTrue();
        await potentialGroupContraventionController
            .doCancelGroupPcnForChangePlate(props)
            .then((_) => {
                updateGrPcIds(props.Id, 'Cancel');
                pushSuccess('Cancel PCN successfully ');
                handleNext();
            })
            .catch(() => {
                backdrop.setFalse();
            })
            .finally(() => {});
    };
    const [throttleCancelDuplicate] = useThrottle(handleCancelDuplicate, 2000);
    const handleClose = () => {
        setIsLoadingLookUp(false);
        setIsShowLookup(false);
        setIsShowForeignPlate(false);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        const cursorPosition = event.target.selectionStart;
        
        setVrnPlate(inputValue.toUpperCase());
        setIsShowLookup(false);
        setIsShowForeignPlate(false);
        setIsForeignPlate(false);

        setTimeout(() => {
            if (event.target && cursorPosition !== null) {
                event.target.setSelectionRange(cursorPosition, cursorPosition);
            }
        }, 0);
    };

    const handleUpdateChange = async (nextVrn?: string) => {
        const vrnToUse = nextVrn ?? vrnPlate;
        if (!vrnToUse) return;
        await handleChangePlate({ ...vehicleLookup, Vrn: vrnToUse }, isForeignPlate);
    };

    const handleGetListNote = async () => {
        if (!selectedPc?.Camera?.Id) return;
        setIsLoading(true);
        await cameraNoteHttpController
            .list({
                page: filter.page ?? 1,
                CameraId: selectedPc?.Camera?.Id,
                pageSize: 8,
                From: filter.from ?? undefined,
                To: filter.to ?? undefined,
                filter: {
                    CameraId: selectedPc?.Camera?.Id,
                    Type: CameraNoteType.Verification,
                },
                sorts: ['-Updated'],
            })
            .then((res) => {
                setListNotes(res);
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleChangeCamera = (camera: Camera) => {
        if (!camera) return;
        setSelectedCamera(camera);
    };

    useEffect(() => {
        if (isLoadingCancellationReasons) return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        handleGetDetailGroupPotentialContravention();
    }, [contraventionId, navigate, isLoadingCancellationReasons]);

    useEffect(() => {
        if (!selectedCamera.Id || !selectedPc?.Camera?.Id) return;
        handleGetListNote();
    }, [filter.from, filter.to, filter.page, selectedCamera.Id, selectedPc?.Camera?.Id]);

    useEffect(() => {
        if (readonly || groupPcIds.length || !step || !zoneId) return;
        const props: IGetGroupPotentialContraventionIds = {
            Step: step,
            ZoneId: zoneId,
            ContraventionDate: { From: contraventionDateFrom!, To: contraventionDateTo! },
            isFilterPcLackVrn: isFilterPcLackVrn ? true : undefined,
            IsPlateGroupMatched: isFilterPcGroupMatched || undefined,
            HasPlate: hasPlate !== null ? hasPlate === 'true' : undefined,
            Confidence: confidence ?? undefined,
        };
        if (!contraventionDateFrom || !contraventionDateTo) {
            delete props.ContraventionDate;
        }
        potentialGroupContraventionController.getGroupPotentialContraventionIds(props).then((res) => {
            setGroupPcIds(res.Ids.map((id) => ({ Id: id })));
        });
    }, [groupPcIds.length, zoneId, step, readonly, contraventionDateFrom, contraventionDateTo, confidence]);

    useEffect(() => {
        if (!groupPcIds.length || !contraventionId) return;
        prefetchNextGroupPc(contraventionId, groupPcIds);
        prefetchPrevGroupPc(contraventionId, groupPcIds);
    }, [contraventionId, groupPcIds, prefetchNextGroupPc, prefetchPrevGroupPc]);

    return {
        contraventionId,
        groupPc,
        setGroupPc,
        selectedPc,
        setSelectedPc,
        isLatestItem,
        updatingPlate,
        skippedList,
        cancellationReasons,
        handleNext,
        handleContinue,
        handleChangePlate,
        handleChangeForeign,
        throttleApprove,
        throttleCancel,
        duplicate,
        setDuplicate,
        throttleCancelDuplicate,
        popupChangePlace,
        setIsLoadingLookUp,
        setIsShowLookup,
        setIsShowForeignPlate,
        isLoadingLookUp,
        isShowLookup,
        isShowForeignPlate,
        handleClose,
        isForeignPlate,
        setIsForeignPlate,
        updateChange,
        setUpdateChange,
        vehicleLookup,
        setVehicleLookup,
        vrn: vrnPlate,
        setVrn: setVrnPlate,
        handleInputChange,
        handleUpdateChange,
        handleChangePc,
        handleChangeCamera,
        conditionExemptionsEnabled,

        conditionAudit,
        contraventionReason,
        toDate,
        fromDate,
        zonesIds,
        emailVerifier,
        setIsLatestItem,
        searchParams,

        updateGrPcIds,

        handlePrev,
        canGoPrev,

        filter,
        setFilter,
        isLoading,
        setIsLoading,
        listNotes,
        setListNotes,
        groupPcIds,
        setGroupPcIds,
        selectedCamera,
        contraventionDateFrom,
        contraventionDateTo,
    };
}
type PotentialContraventionContextType = ReturnType<typeof usePotentialContraventionContext>;

export const PotentialContraventionContext = createContext<PotentialContraventionContextType>(
    {} as PotentialContraventionContextType
);

export const usePotentialContravention = () => useContext(PotentialContraventionContext);
